/**
 * The browser half of renewing a sign-in without stranding the tab (punch list
 * F197). The decisions are `@scheduler/shared` (signInRenewal.ts), tested
 * there; this adds this tab's sessionStorage and whether renewal has failed.
 */
import { useSyncExternalStore } from "react";

import {
  type FlagStore,
  RENEWAL_FAILED_MESSAGE,
  claimReauth as claimIn,
  clearReauthFlag as clearIn,
} from "@scheduler/shared";

function sessionStore(): FlagStore | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Forget that this tab has been sent to sign in. */
export function clearReauthFlag(): void {
  clearIn(sessionStore());
}

/** Record that this tab is being sent to sign in, and say whether it already was. */
export function claimReauth(): boolean {
  return claimIn(sessionStore());
}

export class SignInRenewalError extends Error {
  constructor() {
    super(RENEWAL_FAILED_MESSAGE);
    this.name = "SignInRenewalError";
  }
}

/*
 * Whether this tab's sign-in could not be renewed. Once true it stays true
 * until the page is left: the way back is Sign in again (SignInAgainBar.tsx).
 */
let failed = false;
const listeners = new Set<() => void>();

export function markRenewalFailed(): void {
  if (failed) return;
  failed = true;
  for (const listener of listeners) listener();
}

function renewalFailed(): boolean {
  return failed;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Re-renders when renewal fails. */
export function useRenewalFailed(): boolean {
  return useSyncExternalStore(subscribe, renewalFailed, renewalFailed);
}
